// Import styles
import './styles/main.css';

// Import html2pdf for PDF generation
import html2pdf from 'html2pdf.js';

// Import images
import icXpay from './assets/images/ic_xpay.svg';

// Constants for state statuses
const IDLE = 'IDLE';
const PENDING = 'PENDING';
const SUCCESS = 'SUCCESS';
const ERROR = 'ERROR';

// State management
let userInput = {
    ident: '',  // Номер договору (вводиться першим)
    value: '',  // Прізвище (вводиться другим)
    confirm: false  // Чи підтверджено номер договору
};

const state = {
    status: IDLE,
    error: '',
    data: [],
    disabled: false
};

// DOM elements
const inputElement = document.querySelector('.debt__input');
const submitButton = document.querySelector('.debt__submit');
const formElement = document.querySelector('.debt__form');

// Utility functions
const formatCurrency = (amount) => {
    return `${new Intl.NumberFormat('uk-UA', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount)} грн`;
};

const dateFormatter = new Intl.DateTimeFormat('uk-UA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
});

// API call function
const fetchData = async (data, endpoint) => {
    try {
        state.status = PENDING;
        state.error = '';
        state.data = [];
        state.disabled = true;
        inputElement.disabled = true;
        submitButton.disabled = true;
        updateUI();

        const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });

        const result = await response.json();

        // Handle different status codes
        if (response?.status === 300) {
            // Need surname
            userInput.confirm = true;
            inputElement.placeholder = 'Введіть прізвище';
            state.status = IDLE;
        } else if (response?.status === 200) {
            if (userInput.confirm) {
                // Success - got debt data
                state.status = SUCCESS;
                state.data = result;
            } else {
                // Need surname confirmation
                userInput.confirm = true;
                state.status = IDLE;
                inputElement.placeholder = 'Введіть прізвище';
            }
        } else {
            // Error
            state.status = ERROR;
            state.error = result.message || 'Щось пішло не так.';
        }

        updateUI();
    } catch (error) {
        state.status = ERROR;
        state.error = error.message;
        updateUI();
    } finally {
        inputElement.value = '';
        inputElement.disabled = false;
        submitButton.disabled = false;
    }
};

// Create element helper
function createElement(tag, className, textContent) {
    const element = document.createElement(tag);
    element.className = className;
    if (textContent) element.textContent = textContent;
    return element;
}

// UI update function
const updateUI = () => {
    const infoContainer = document.getElementById('debt_info');
    infoContainer.innerHTML = '';

    // IDLE state
    if (state.status === IDLE) {
        if (userInput.confirm) {
            infoContainer.appendChild(
                createElement('p', 'paragraph debt__description', 'Введіть прізвище для підтвердження.')
            );
        } else {
            infoContainer.appendChild(
                createElement('p', 'paragraph debt__description', 'Щоб дізнатися, чи є у Вас заборгованість, введіть номер договору. Наприклад: 12-34 або 4-279 👆')
            );
        }
        return;
    }

    // PENDING state
    if (state.status === PENDING) {
        infoContainer.appendChild(
            createElement('p', 'paragraph debt__description', 'Завантаження...')
        );
        return;
    }

    // ERROR state
    if (state.status === ERROR) {
        infoContainer.appendChild(
            createElement('p', 'paragraph debt__description', `${state.error}`)
        );
        return;
    }

    // SUCCESS state
    if (state.status === SUCCESS) {
        const debts = state.data?.data;

        if (!debts || debts.length === 0) {
            infoContainer.appendChild(
                createElement('p', 'paragraph debt__description', 'Дані заборгованості відсутні.')
            );
            return;
        }

        const firstDebt = debts[0];

        // Container for debt info
        const debtInfoContainer = createElement('div', 'debt__info-card');

        // === HEADER ROW ===
        const headerContainer = createElement('div', 'debt__header-row');
        const headerLeft = createElement('div', 'debt__header-left');
        const headerRight = createElement('div', 'debt__header-right');

        headerLeft.textContent = `Заборгованість станом на ${dateFormatter.format(new Date(firstDebt.date))}`;
        headerRight.textContent = 'Сплатити зручно онлайн:';

        headerContainer.appendChild(headerLeft);
        headerContainer.appendChild(headerRight);
        debtInfoContainer.appendChild(headerContainer);

        // === ІМ'Я СТУДЕНТА ===
        debtInfoContainer.appendChild(
            createElement('h3', 'paragraph paragraph--lg debt__info-card-value debt__taxpayer-name', firstDebt.ntab)
        );

        // === ФУНКЦІЯ ДЛЯ СТВОРЕННЯ РЯДКА ПОДАТКУ ===
        const createTaxRow = (title, amount, paymentUrl = null) => {
            if (paymentUrl && amount > 0) {
                // Створюємо flexbox рядок з кнопкою
                const rowContainer = document.createElement('div');
                rowContainer.className = 'debt__tax-row-with-button';

                const taxDetails = document.createElement('div');
                taxDetails.className = 'debt__tax-details';
                taxDetails.appendChild(createElement('p', 'paragraph paragraph--sm debt__info-card-type', title));
                taxDetails.appendChild(createElement('p', 'paragraph debt__info-card-value', formatCurrency(amount)));

                const payButton = document.createElement('a');
                payButton.href = paymentUrl;
                payButton.target = '_blank';
                payButton.rel = 'noopener noreferrer';
                payButton.className = 'debt__pay-button';
                payButton.textContent = 'Сплатити';

                rowContainer.appendChild(taxDetails);
                rowContainer.appendChild(payButton);

                return rowContainer;
            } else if (amount > 0) {
                // Звичайний рядок без кнопки (якщо немає URL але є борг)
                const rowContainer = document.createElement('div');
                rowContainer.className = 'debt__tax-row';
                rowContainer.appendChild(createElement('p', 'paragraph paragraph--sm debt__info-card-type', title));
                rowContainer.appendChild(createElement('p', 'paragraph debt__info-card-value', formatCurrency(amount)));
                return rowContainer;
            }
            return null; // Не показуємо рядок якщо немає боргу
        };

        // === СТВОРЮЄМО РЯДКИ ПОДАТКІВ ДЛЯ КОЖНОГО БОРГУ ===
        debts.forEach((debt) => {
            const row = createTaxRow(debt.service, debt.skopkin, debt.url);
            if (row) {
                debtInfoContainer.appendChild(row);
            }
        });

        infoContainer.appendChild(debtInfoContainer);

        // === РЕКВІЗИТИ ДЛЯ ОПЛАТИ ===
        const paymentDetails = createElement('div', 'debt__info-payment-details');
        paymentDetails.appendChild(createElement('h2', 'title', 'Реквізити для оплати'));

        const buttonsContainer = createElement('div', 'debt__payment-buttons');
        buttonsContainer.appendChild(createElement('button', 'btn debt__info-payment-details-btn', 'Показати реквізити'));
        buttonsContainer.appendChild(createElement('button', 'btn debt__pdf-download-btn', 'Завантажити PDF'));

        paymentDetails.appendChild(buttonsContainer);
        paymentDetails.appendChild(createElement('div', 'debt__info-payment-details-list'));
        infoContainer.appendChild(paymentDetails);
    }
};

// Initialize UI
updateUI();

// Event listeners
inputElement.addEventListener('change', (e) => {
    if (userInput.confirm) {
        userInput.value = e.target.value;  // Прізвище (другий крок)
    } else {
        userInput.ident = e.target.value;  // Номер договору (перший крок)
    }
});

formElement.addEventListener('submit', async (e) => {
    e.preventDefault();

    if (userInput.confirm) {
        // Валідація прізвища (лише одне слово, мінімум 2 символи)
        const surname = userInput.value?.trim();
        const surnamePattern = /^[А-ЯІЇЄҐа-яіїєґA-Za-z\-']+$/;

        if (surname && surname.length >= 2 && surnamePattern.test(surname)) {
            await fetchData({
                ident: userInput.ident,
                username: surname
            }, '/confirm');
        } else {
            state.status = ERROR;
            state.error = 'Будь ласка, введіть коректне прізвище (наприклад: Кравецький)';
            inputElement.value = '';
            updateUI();
        }
    } else {
        // Валідація номера договору
        const contractPattern = /^[\d\-]+$/;
        const cleanedContract = userInput.ident.trim();

        if (cleanedContract.length >= 1 && cleanedContract.length <= 20 && contractPattern.test(cleanedContract)) {
            await fetchData({ ident: cleanedContract }, '/info');
        } else {
            state.status = ERROR;
            state.error = 'Будь ласка, введіть коректний номер договору (наприклад: 12-34, 4-279, 1-0-1)';
            inputElement.value = '';
            updateUI();
        }
    }
});

// Функція для генерації PDF з реквізитами
const generatePDF = () => {
    const debts = state.data?.data;
    if (!debts || debts.length === 0) return;

    const firstDebt = debts[0];

    // Створюємо HTML контейнер для PDF
    const pdfContent = document.createElement('div');
    pdfContent.style.padding = '20px';
    pdfContent.style.fontFamily = 'Arial, sans-serif';
    pdfContent.style.color = '#000';
    pdfContent.style.backgroundColor = '#fff';

    // Додаємо заголовок
    const header = document.createElement('div');
    header.style.textAlign = 'center';
    header.style.marginBottom = '20px';
    header.innerHTML = `
        <h1 style="font-size: 24px; margin-bottom: 10px;">Реквізити для оплати</h1>
        <p style="font-size: 14px; color: #666;">Станом на ${dateFormatter.format(new Date(firstDebt.date))}</p>
    `;
    pdfContent.appendChild(header);

    // Додаємо ім'я студента
    const payerInfo = document.createElement('div');
    payerInfo.style.marginBottom = '20px';
    payerInfo.innerHTML = `
        <h3 style="font-size: 18px; margin-bottom: 10px;">Платник</h3>
        <p style="font-size: 14px;">${firstDebt.ntab}</p>
    `;
    pdfContent.appendChild(payerInfo);

    // Додаємо кожен борг з реквізитами
    debts.forEach((debt, index) => {
        const debtSection = document.createElement('div');
        debtSection.style.marginBottom = '30px';
        debtSection.style.pageBreakInside = 'avoid';
        debtSection.style.borderTop = index > 0 ? '1px solid #ddd' : 'none';
        debtSection.style.paddingTop = index > 0 ? '20px' : '0';

        debtSection.innerHTML = `
            <h3 style="font-size: 16px; margin-bottom: 15px; color: #333;">${debt.service}</h3>
            <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
                <tr>
                    <td style="padding: 8px 0; font-weight: bold; width: 40%;">Сума:</td>
                    <td style="padding: 8px 0;">${formatCurrency(debt.skopkin)}</td>
                </tr>
                ${debt.recipient ? `
                <tr>
                    <td style="padding: 8px 0; font-weight: bold;">Отримувач:</td>
                    <td style="padding: 8px 0;">${debt.recipient}</td>
                </tr>` : ''}
                ${debt.edrpou ? `
                <tr>
                    <td style="padding: 8px 0; font-weight: bold;">ЄДРПОУ:</td>
                    <td style="padding: 8px 0;">${debt.edrpou}</td>
                </tr>` : ''}
                ${debt.account ? `
                <tr>
                    <td style="padding: 8px 0; font-weight: bold;">Рахунок (IBAN):</td>
                    <td style="padding: 8px 0; word-break: break-all;">${debt.account}</td>
                </tr>` : ''}
                ${debt.purpose ? `
                <tr>
                    <td style="padding: 8px 0; font-weight: bold;">Призначення платежу:</td>
                    <td style="padding: 8px 0;">${debt.purpose}</td>
                </tr>` : ''}
            </table>
        `;
        pdfContent.appendChild(debtSection);
    });

    // Налаштування для html2pdf
    const opt = {
        margin: 10,
        filename: `Реквізити_${firstDebt.ntab.replace(/\s+/g, '_')}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    // Генеруємо і завантажуємо PDF
    html2pdf().set(opt).from(pdfContent).save();
};

// Show payment details or download PDF
document.addEventListener('click', (e) => {
    // Show payment details
    if (e.target && e.target.classList.contains('debt__info-payment-details-btn')) {
        const debts = state.data?.data;
        const button = document.querySelector('.debt__info-payment-details-btn');
        const detailsList = document.querySelector('.debt__info-payment-details-list');

        button.disabled = true;
        detailsList.style.opacity = 1;
        detailsList.style.paddingTop = '24px';

        if (!debts || debts.length === 0) {
            detailsList.innerHTML = '<p class="paragraph debt__description">Дані про заборгованість відсутні.</p>';
            return;
        }

        // Render payment details for each debt
        debts.forEach((debt, index) => {
            const detailCard = `
                <div class="debt__info-card debt__info-card--payment">
                    <h3 class="title debt__info-card-title">${debt.service}</h3>
                    <p class="paragraph paragraph--sm debt__info-card-type">Платник</p>
                    <h3 class="paragraph paragraph--lg debt__info-card-value">${debt.ntab}</h3>
                    <p class="paragraph paragraph--sm debt__info-card-type">Сума</p>
                    <p class="paragraph debt__info-card-value">${formatCurrency(debt.skopkin)}</p>
                    ${debt.recipient ? `
                    <p class="paragraph paragraph--sm debt__info-card-type">Отримувач</p>
                    <p class="paragraph debt__info-card-value">${debt.recipient}</p>` : ''}
                    ${debt.edrpou ? `
                    <p class="paragraph paragraph--sm debt__info-card-type">ЄДРПОУ</p>
                    <p class="paragraph debt__info-card-value">${debt.edrpou}</p>` : ''}
                    ${debt.account ? `
                    <p class="paragraph paragraph--sm debt__info-card-type">Рахунок (IBAN)</p>
                    <p class="paragraph debt__info-card-value">${debt.account}</p>` : ''}
                    ${debt.purpose ? `
                    <p class="paragraph paragraph--sm debt__info-card-type">Призначення платежу</p>
                    <p class="paragraph debt__info-card-value">${debt.purpose}</p>` : ''}
                </div>
            `;
            detailsList.innerHTML += detailCard;
        });
    }

    // Download PDF
    if (e.target && e.target.classList.contains('debt__pdf-download-btn')) {
        generatePDF();
    }
});

// DOMContentLoaded event
window.addEventListener('DOMContentLoaded', () => {
    // Remove loading screen
    const removeLoadingScreen = () => {
        document.body.style.overflow = 'auto';
        const loadingScreen = document.querySelector('.loadingScreen');
        loadingScreen.classList.add('loadingScreen--hidden');

        setTimeout(() => {
            loadingScreen.remove();
            document.querySelector('#loadingScreenStyles').remove();
        }, 350);
    };

    removeLoadingScreen();

    // Lazy load images
    const setupLazyLoading = () => {
        const lazyImages = document.querySelectorAll('.lazy-image');

        const imageObserver = new IntersectionObserver((entries, observer) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const container = entry.target;
                    const img = container.querySelector('img');
                    const dataSrc = img.getAttribute('data-src');

                    // Dynamically import image
                    import(`./assets/images/${dataSrc}`)
                        .then(module => {
                            img.setAttribute('src', module.default);
                            img.addEventListener('load', () => {
                                container.classList.add('image-loaded');
                            });
                        });

                    observer.unobserve(img);
                }
            });
        }, {
            threshold: 0.05
        });

        lazyImages.forEach(img => imageObserver.observe(img));
    };

    setupLazyLoading();

    // Active navigation links on scroll
    const sections = document.querySelectorAll('.section');

    const handleNavigation = (section) => {
        const rect = section.getBoundingClientRect();
        const navLink = document.querySelector(`[data-anchor][href='#${section.id}']`);

        if (!navLink) return;

        const activate = () => {
            navLink.classList.add('navigation__link--active');
        };

        const deactivate = () => {
            navLink.classList.remove('navigation__link--active');
        };

        // Check if section is in viewport
        if (rect.top < window.innerHeight / 2 && rect.top > 0) {
            activate();
        } else if (rect.top > window.innerHeight / 2) {
            deactivate();
        } else if (rect.bottom > window.innerHeight / 2) {
            activate();
        } else if (rect.bottom < window.innerHeight / 2 && rect.bottom > 0) {
            deactivate();
        }
    };

    sections.forEach(section => {
        handleNavigation(section);
        window.addEventListener('scroll', () => handleNavigation(section));
    });
});
